import { useState, useEffect, useLayoutEffect, useRef } from "react";

import { Button, Tooltip } from "@bigbinary/neeto-atoms";
import classnames from "classnames";
import { Copy, Check } from "lucide-react";
import { copyToClipboard } from "neetocommons/utils/general";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { TIME_OUT } from "./constants";

const CopyToClipboardButton = ({
  className,
  icon: Icon = Copy,
  label = "",
  successLabel,
  tooltipContent,
  successTooltipContent,
  value,
  variant = "outline",
  size,
  tooltipProps,
  onClick,
  ...otherProps
}) => {
  const { t } = useTranslation();

  const [isChecked, setIsChecked] = useState(false);

  const ref = useRef();
  const timerRef = useRef();

  const resolvedSize = size || (label ? "default" : "icon");

  const onHandleClick = event => {
    event.preventDefault();
    event.stopPropagation();

    if (onClick) onClick(event);
    else copyToClipboard(value, { showToastr: false });

    clearTimeout(timerRef.current);
    setIsChecked(true);
    timerRef.current = setTimeout(() => {
      setIsChecked(false);
    }, TIME_OUT);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.style.minWidth = "";
    const buttonWidth = ref.current.clientWidth;
    ref.current.style.minWidth = `${buttonWidth}px`;
  }, [resolvedSize, label]);

  const currentTooltipContent = isChecked
    ? successTooltipContent || t("neetoMolecules.common.copied")
    : tooltipContent || t("neetoMolecules.common.copy");

  const buttonLabel =
    isChecked && label
      ? successLabel || t("neetoMolecules.common.copied")
      : label;
  const ButtonIcon = isChecked ? Check : Icon;

  const button = (
    <Button
      {...{ ref }}
      className={classnames(isChecked && "nv-copy-button-active", className)}
      data-testid="copy-to-clipboard-button"
      icon={ButtonIcon}
      iconPosition="left"
      label={buttonLabel || undefined}
      size={resolvedSize}
      variant={isChecked ? "default" : variant}
      onClick={onHandleClick}
      {...otherProps}
    />
  );

  if (label) return button;

  return (
    <Tooltip content={currentTooltipContent} position="top" {...tooltipProps}>
      {button}
    </Tooltip>
  );
};

CopyToClipboardButton.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.object,
  ]),
  label: PropTypes.string,
  successLabel: PropTypes.string,
  value: PropTypes.string,
  variant: PropTypes.string,
  size: PropTypes.string,
  tooltipContent: PropTypes.string,
  successTooltipContent: PropTypes.string,
};

export default CopyToClipboardButton;
